/**
 * OMON-195 Part A — self-service "Change my password".
 *
 * Any signed-in web user (planner / admin / librarian / auditor) can change
 * their own password from the left-rail "Password" button. Firebase rejects
 * `updatePassword` on a stale session (`auth/requires-recent-login`), so we
 * reauthenticate with the current password first.
 *
 * Pure helpers (`validateChangePassword`, `mapAuthError`) are exported for
 * unit testing; the DOM wiring is not exercised by vitest.
 */
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";

import { auth } from "./firebase";
import { changePasswordBtn, modalContainer } from "./dom";
import { MIN_PASSWORD_LENGTH, validateChangePassword, mapAuthError } from "./account-password";

function escapeHtml(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

export function openChangePasswordModal(): void {
  const user = auth.currentUser;
  if (!user) return; // not signed in — nothing to change
  const email = user.email ?? "";

  modalContainer.innerHTML = `
    <div class="modal-overlay">
      <div class="modal-box change-pw-modal" role="dialog" aria-labelledby="changePwTitle">
        <h3 id="changePwTitle">Change password</h3>
        <p class="edit-role-subject">${escapeHtml(email)}</p>

        <div class="edit-role-form">
          <label class="edit-role-field">
            <span class="edit-role-label">Current password</span>
            <input id="changePwCurrent" type="password" autocomplete="current-password">
          </label>
          <label class="edit-role-field">
            <span class="edit-role-label">New password (min ${MIN_PASSWORD_LENGTH})</span>
            <input id="changePwNew" type="password" autocomplete="new-password">
          </label>
          <label class="edit-role-field">
            <span class="edit-role-label">Confirm new password</span>
            <input id="changePwConfirm" type="password" autocomplete="new-password">
          </label>
        </div>

        <div class="edit-role-status" id="changePwStatus" aria-live="polite"></div>

        <div class="modal-actions">
          <button class="btn-outline" id="changePwCancel">Cancel</button>
          <button class="btn-primary" id="changePwSave">Change password</button>
        </div>
      </div>
    </div>
  `;

  const cleanup = () => {
    modalContainer.innerHTML = "";
    document.removeEventListener("keydown", handleEsc);
  };
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") cleanup();
  };
  document.addEventListener("keydown", handleEsc);

  modalContainer.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-overlay")) cleanup();
  });
  modalContainer.querySelector("#changePwCancel")?.addEventListener("click", cleanup);

  modalContainer.querySelector("#changePwSave")?.addEventListener("click", async () => {
    const cur = modalContainer.querySelector<HTMLInputElement>("#changePwCurrent");
    const nxt = modalContainer.querySelector<HTMLInputElement>("#changePwNew");
    const conf = modalContainer.querySelector<HTMLInputElement>("#changePwConfirm");
    const statusEl = modalContainer.querySelector<HTMLElement>("#changePwStatus");
    const saveBtn = modalContainer.querySelector<HTMLButtonElement>("#changePwSave");
    if (!cur || !nxt || !conf || !statusEl || !saveBtn) return;

    const v = validateChangePassword(cur.value, nxt.value, conf.value);
    if (!v.ok) {
      statusEl.textContent = v.error ?? "Invalid input.";
      return;
    }

    saveBtn.disabled = true;
    statusEl.textContent = "Changing…";
    try {
      // Reauthenticate first — updatePassword needs a fresh session.
      const cred = EmailAuthProvider.credential(email, cur.value);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, nxt.value);
      statusEl.textContent = "Password changed.";
      setTimeout(cleanup, 1200);
    } catch (err) {
      const code =
        err && typeof err === "object" && "code" in err
          ? String((err as { code: unknown }).code)
          : "";
      statusEl.textContent = mapAuthError(code);
      saveBtn.disabled = false;
    }
  });

  modalContainer.querySelector<HTMLInputElement>("#changePwCurrent")?.focus();
}

/** Wire the rail "Password" button. Called once at startup from main.ts. */
export function bindChangePassword(): void {
  changePasswordBtn.addEventListener("click", openChangePasswordModal);
}
